import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Search,
  Rocket,
  Phone,
  MessageSquare,
  Link2,
  Wrench,
  CreditCard,
  ShieldCheck,
  LifeBuoy,
  ArrowRight,
  Clock,
  ChevronRight,
} from "lucide-react";
import DocsNavigation from "../components/landing/DocsNavigation";
import { Footer } from "../components/landing/Footer";
import {
  CATEGORIES,
  ARTICLES,
  searchArticles,
} from "../data/helpArticles";

const ICON_MAP = {
  Rocket,
  Phone,
  MessageSquare,
  Link2,
  Wrench,
  CreditCard,
  ShieldCheck,
  LifeBuoy,
};

const POPULAR_SLUGS = [
  "quick-start-5-minutes",
  "build-your-first-voice-agent",
  "appointment-booking",
  "embed-chat-widget",
  "tcpa-recording-consent-dnc",
  "agent-not-answering-inbound",
];

const HelpCenterPage = () => {
  const [query, setQuery] = useState("");

  useEffect(() => {
    const html = document.documentElement;
    const wasLight = html.classList.contains("light");
    if (wasLight) {
      html.classList.remove("light");
      html.classList.add("dark");
    }
    window.scrollTo(0, 0);
    return () => {
      if (wasLight) {
        html.classList.remove("dark");
        html.classList.add("light");
      }
    };
  }, []);

  const searchResults = useMemo(() => searchArticles(query), [query]);
  const popularArticles = useMemo(
    () =>
      POPULAR_SLUGS.map((s) => ARTICLES.find((a) => a.slug === s)).filter(
        Boolean
      ),
    []
  );

  return (
    <div
      className="min-h-screen bg-space-900 relative"
      data-testid="help-center-page"
    >
      <DocsNavigation />

      <main className="relative pt-28 pb-24 px-6 md:px-12">
        {/* Hero */}
        <section className="max-w-5xl mx-auto text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-400/10 border border-teal-400/20 text-xs text-teal-300 font-medium mb-6">
            Voxinity AI Help Center
          </div>
          <h1 className="font-display font-semibold text-4xl sm:text-5xl lg:text-6xl text-white mb-5 leading-tight">
            Everything you need to launch,
            <br className="hidden md:block" /> tune, and scale your AI agents.
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-10">
            Step-by-step guides written by the team that builds Voxinity.
            Search a topic or browse by category.
          </p>

          <div className="relative max-w-xl mx-auto">
            <Search
              size={18}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500"
            />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search the docs (e.g., booking, TCPA, low balance)"
              className="w-full pl-11 pr-4 py-3.5 rounded-full bg-white/[0.04] border border-white/[0.08] text-white placeholder:text-gray-500 focus:outline-none focus:border-teal-400/40 focus:bg-white/[0.06] transition-all"
              data-testid="help-search-input"
            />
          </div>

          {/* Search Results */}
          {query.trim() && (
            <div className="mt-6 max-w-2xl mx-auto text-left">
              {searchResults.length === 0 ? (
                <p className="text-gray-500 text-sm">
                  No results for "{query}". Try a different keyword or{" "}
                  <Link
                    to="/support"
                    className="text-teal-400 hover:underline"
                  >
                    contact support
                  </Link>
                  .
                </p>
              ) : (
                <div className="space-y-2">
                  {searchResults.map((a) => (
                    <Link
                      key={a.slug}
                      to={`/help/${a.slug}`}
                      className="flex items-center justify-between p-4 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:border-teal-400/30 hover:bg-white/[0.05] transition-all group"
                      data-testid={`help-search-result-${a.slug}`}
                    >
                      <div>
                        <div className="text-white font-medium">{a.title}</div>
                        <div className="text-sm text-gray-500 mt-0.5">
                          {a.description}
                        </div>
                      </div>
                      <ChevronRight
                        size={18}
                        className="text-gray-500 group-hover:text-teal-300 group-hover:translate-x-1 transition-all flex-shrink-0"
                      />
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>

        {!query.trim() && (
          <>
            {/* Categories Grid */}
            <section className="max-w-6xl mx-auto mb-20">
              <h2 className="font-display font-semibold text-2xl text-white mb-6">
                Browse by topic
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {CATEGORIES.map((cat) => {
                  const Icon = ICON_MAP[cat.icon] || LifeBuoy;
                  const count = ARTICLES.filter(
                    (a) => a.category === cat.id
                  ).length;
                  return (
                    <Link
                      key={cat.id}
                      to={`/help/category/${cat.id}`}
                      className="group p-5 rounded-2xl bg-white/[0.03] border border-white/[0.06] hover:border-teal-400/30 hover:bg-white/[0.05] transition-all"
                      data-testid={`help-category-${cat.id}`}
                    >
                      <div
                        className={`w-11 h-11 rounded-xl bg-gradient-to-br ${cat.color} bg-opacity-20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}
                      >
                        <Icon size={20} className="text-white" />
                      </div>
                      <h3 className="text-white font-semibold mb-1.5">
                        {cat.title}
                      </h3>
                      <p className="text-sm text-gray-400 leading-relaxed mb-3">
                        {cat.description}
                      </p>
                      <div className="text-xs text-teal-300 font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                        {count} article{count !== 1 ? "s" : ""}
                        <ArrowRight size={12} />
                      </div>
                    </Link>
                  );
                })}
              </div>
            </section>

            {/* Popular Articles */}
            <section className="max-w-6xl mx-auto">
              <h2 className="font-display font-semibold text-2xl text-white mb-6">
                Most popular articles
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {popularArticles.map((a) => (
                  <Link
                    key={a.slug}
                    to={`/help/${a.slug}`}
                    className="group flex items-center justify-between p-4 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:border-teal-400/30 hover:bg-white/[0.05] transition-all"
                    data-testid={`help-popular-${a.slug}`}
                  >
                    <div className="flex-1 min-w-0 pr-3">
                      <div className="text-white font-medium truncate">
                        {a.title}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                        <Clock size={11} />
                        {a.readTime} min read
                      </div>
                    </div>
                    <ChevronRight
                      size={18}
                      className="text-gray-500 group-hover:text-teal-300 group-hover:translate-x-1 transition-all flex-shrink-0"
                    />
                  </Link>
                ))}
              </div>
            </section>

            {/* Still Need Help CTA */}
            <section className="max-w-3xl mx-auto mt-24 text-center">
              <div className="p-10 rounded-3xl bg-gradient-to-br from-teal-400/[0.06] to-teal-400/[0.02] border border-teal-400/20">
                <h3 className="font-display font-semibold text-2xl text-white mb-2">
                  Can't find what you need?
                </h3>
                <p className="text-gray-400 mb-6">
                  Our team usually replies within 1 business day.
                </p>
                <Link
                  to="/support"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-teal-400 hover:bg-teal-500 text-space-900 font-medium transition-colors"
                  data-testid="help-contact-support-cta"
                >
                  Contact Support
                  <ArrowRight size={16} />
                </Link>
              </div>
            </section>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default HelpCenterPage;
